import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import { ScreenLayout, Header } from '../components';
import { Input, Button, CurrencyPicker } from '../components/design-system';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { businessCurrency } from '../utils/businessDefaults';

interface BulkImportScreenProps {
  catalogueId: string;
}

export const BulkImportScreen: React.FC<BulkImportScreenProps> = ({
  catalogueId,
}) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, activeTab, setActiveTab, routeParams } = useAppContext();
  const [loading, setLoading] = useState(false);
  const [fileData, setFileData] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [fileMimetype, setFileMimetype] = useState<string>('');
  const [currency, setCurrency] = useState<string>(() => businessCurrency());

  const listId: string | undefined = routeParams?.listId;

  const finishAndNavigate = async (createdIds: string[]) => {
    if (listId) {
      if (createdIds.length > 0) {
        try {
          await apiService.linkDataListRecords(listId, createdIds);
        } catch (err) {
          console.error('[BulkImportScreen] Failed to link imported products to data list:', err);
        }
      }
      navigateTo('data-list-records', listId);
    } else if (Platform.OS === 'web') {
      window.history.back();
    } else {
      navigateTo('products');
    }
  };

  const pickFile = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        const asset = result.assets[0];
        setFileName(asset.name);
        setFileMimetype(asset.mimeType || 'text/csv');

        // Read file as base64
        if (Platform.OS === 'web') {
          // For web, we need to read the file differently
          const response = await fetch(asset.uri);
          const blob = await response.blob();
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64 = reader.result as string;
            setFileData(base64);
          };
          reader.readAsDataURL(blob);
        } else {
          // For mobile, read file
          const response = await fetch(asset.uri);
          const blob = await response.blob();
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64 = reader.result as string;
            setFileData(base64);
          };
          reader.readAsDataURL(blob);
        }
      }
    } catch (error: any) {
      console.error('Error picking file:', error);
      showNotification('error', 'Error', 'Failed to pick file. Please try again.');
    }
  };

  const handleImport = async () => {
    if (!fileData || !fileName) {
      showNotification('error', 'Error', 'Please select a file to import');
      return;
    }

    try {
      setLoading(true);

      // For now, we'll send the file data directly
      // The backend will parse it
      const response = await apiService.authenticatedRequest<{
        success: any[];
        failed: Array<{ index: number; error: string }>;
      }>('/api/omni-channel/products/bulk-import', {
        method: 'POST',
        body: JSON.stringify({
          catalogueId,
          fileData,
          fileName,
          fileMimetype,
          currency,
        }),
      });

      if (response.statusCode === 1000 && response.data) {
        // Handle both old format (success/failed arrays) and new format (importSummary)
        const importSummary = response.data.importSummary || response.data;
        const success = response.data.success || [];
        const failed = response.data.failed || [];
        const invalidRows = importSummary?.invalidRows || [];
        
        const successCount = success.length || importSummary?.valid || 0;
        const failedCount = failed.length || importSummary?.invalid || 0;
        const createdIds: string[] = success.map((p: any) => p?._id || p?.id).filter(Boolean);

        if (failedCount === 0) {
          showNotification('success', 'Success', `Successfully imported ${successCount} products.`);
          setTimeout(() => finishAndNavigate(createdIds), 1500);
        } else {
          // Show detailed error notification
          let errorMessage = `Imported ${successCount} products successfully.`;
          if (failedCount > 0) {
            errorMessage += `

${failedCount} product(s) failed to import:`;
            if (invalidRows.length > 0) {
              // Show first 3 errors
              const errorsToShow = invalidRows.slice(0, 3);
              errorsToShow.forEach((row: any) => {
                errorMessage += `
Row ${row.row}: ${row.errors?.join(', ') || 'Validation failed'}`;
              });
              if (invalidRows.length > 3) {
                errorMessage += `
... and ${invalidRows.length - 3} more error(s)`;
              }
            } else {
              errorMessage += `
${failedCount} product(s) had validation errors.`;
            }
          }
          
          showNotification(
            'warning',
            'Partial Import',
            errorMessage
          );
          console.log('Failed products:', failed);
          setTimeout(() => finishAndNavigate(createdIds), 2000);
        }
      } else {
        const errorMessage =
          response.errors?.[0]?.message || 'Failed to import products';
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      console.error('Error importing products:', error);
      showNotification('error', 'Error', error.message || 'Failed to import products');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (listId) {
      navigateTo('data-list-records', listId);
    } else if (Platform.OS === 'web') {
      window.history.back();
    } else {
      navigateTo('products');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setLoading(true);
      const response = await apiService.downloadProductTemplate();

      if (response.statusCode === 1000 && response.data) {
        if (Platform.OS === 'web') {
          // Web: Create blob and trigger download
          const blob = response.data as Blob;
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'products_template.csv';
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          
          showNotification('success', 'Template Downloaded', 'Check your downloads folder for products_template.csv');
        } else {
          // Mobile: Show template format info
          showNotification(
            'info',
            'Template Format',
            'Download the template from the web version or create a CSV with the columns shown below.'
          );
        }
      } else {
        const errorMessage = response.errors?.[0]?.message || 'Failed to download template';
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      console.error('Error downloading template:', error);
      showNotification('error', 'Error', error.message || 'Failed to download template');
    } finally {
      setLoading(false);
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
    >
      <ScrollView 
        style={styles.container}
        contentContainerStyle={[
          styles.contentContainer,
          Platform.OS === 'web' && styles.contentContainerWeb,
          Platform.OS !== 'web' && styles.contentContainerMobile
        ]}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.content}>
          <Text style={styles.title}>Bulk Import Products</Text>
          <Text style={styles.subtitle}>
            Upload a CSV or Excel file to import multiple products at once
          </Text>

          {/* File Selection */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Select File</Text>
            <TouchableOpacity
              style={styles.fileButton}
              onPress={pickFile}
              disabled={loading}
            >
              <Ionicons
                name="document-attach-outline"
                size={24}
                color={colors.primary}
              />
              <Text style={styles.fileButtonText}>
                {fileName || 'Choose CSV or Excel file'}
              </Text>
            </TouchableOpacity>
            {fileName && (
              <View style={styles.fileInfo}>
                <Ionicons name="checkmark-circle" size={16} color={colors.success} />
                <Text style={styles.fileInfoText}>{fileName}</Text>
                <TouchableOpacity
                  onPress={() => {
                    setFileName('');
                    setFileData(null);
                    setFileMimetype('');
                  }}
                >
                  <Ionicons name="close-circle" size={16} color={colors.error} />
                </TouchableOpacity>
              </View>
            )}
          </View>

          {/* Currency */}
          <View style={styles.section}>
            <CurrencyPicker
              label="Currency"
              value={currency}
              onChange={setCurrency}
            />
          </View>

          {/* Instructions */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>File Format</Text>
              <TouchableOpacity
                style={styles.downloadButton}
                onPress={handleDownloadTemplate}
                disabled={loading}
              >
                <Ionicons name="download-outline" size={18} color={colors.primary} />
                <Text style={styles.downloadButtonText}>Download Template</Text>
              </TouchableOpacity>
            </View>
            <Text style={styles.instructionText}>
              Your CSV/Excel file should include the following columns:
            </Text>
            <View style={styles.columnsList}>
              {[
                'name (required)',
                'description',
                'brand',
                'sku',
                'price (required)',
                'salePrice',
                'inventoryQuantity',
                'availability',
                'condition',
                'images (comma-separated URLs)',
              ].map((col, index) => (
                <View key={index} style={styles.columnItem}>
                  <Ionicons name="checkmark" size={16} color={colors.primary} />
                  <Text style={styles.columnText}>{col}</Text>
                </View>
              ))}
            </View>
          </View>

          {/* Actions */}
          <View style={styles.actions}>
            <Button
              title="Cancel"
              onPress={handleCancel}
              variant="outline"
              style={styles.cancelButton}
              disabled={loading}
            />
            <Button
              title="Import Products"
              onPress={handleImport}
              variant="primary"
              loading={loading}
              disabled={loading || !fileData}
              style={styles.importButton}
            />
          </View>
        </View>
      </ScrollView>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
  },
  contentContainer: {
    padding: spacing.lg,
    paddingBottom: spacing.xl,
  },
  contentContainerMobile: {
    paddingBottom: 100, // Extra padding for mobile to avoid bottom tabs overlay
  },
  contentContainerWeb: {
    paddingBottom: spacing.sm, // Minimal padding on web (ScreenLayout handles spacing)
  },
  content: {
    padding: spacing.md,
  },
  title: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: '600',
    color: colors.textDark,
    marginBottom: spacing.xs,
    fontFamily: typography.fontFamily.semibold,
  },
  subtitle: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    marginBottom: spacing.lg,
    fontFamily: typography.fontFamily.primary,
  },
  section: {
    backgroundColor: colors.background.surface,
    padding: spacing.md,
    borderRadius: designTokens.borderRadius.md,
    marginBottom: spacing.md,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.textDark,
    marginBottom: spacing.md,
    fontFamily: typography.fontFamily.semibold,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  downloadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: designTokens.borderRadius.sm,
    backgroundColor: colors.lightGray,
  },
  downloadButtonText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
  fileButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.lg,
    borderWidth: 2,
    borderColor: colors.border,
    borderStyle: 'dashed',
    borderRadius: designTokens.borderRadius.md,
    gap: spacing.sm,
  },
  fileButtonText: {
    fontSize: typography.fontSize.base,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
  fileInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: spacing.sm,
    gap: spacing.sm,
  },
  fileInfoText: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.primary,
  },
  instructionText: {
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    marginBottom: spacing.md,
    fontFamily: typography.fontFamily.primary,
  },
  columnsList: {
    gap: spacing.sm,
  },
  columnItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  columnText: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.primary,
  },
  actions: {
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: spacing.md,
    marginBottom: spacing.xl,
  },
  cancelButton: {
    flex: 1,
  },
  importButton: {
    flex: 1,
  },
});

